import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getDocumentVisibilityListBadge } from './documentVisibility';
import {
  HiDocumentText,
  HiInformationCircle,
  HiEye,
  HiArrowDownTray,
  HiLink,
  HiPencilSquare,
  HiArrowPath,
  HiTrash,
  HiGlobeAlt,
  HiChevronDown,
  HiChevronUp,
  HiEllipsisVertical,
} from 'react-icons/hi2';

const MAX_NOTES_LENGTH = 100;

export default function DocumentListItem({
  doc,
  isPreviewable,
  canManageDoc,
  openDetails,
  openPreview,
  openShare,
  openRename,
  openReupload,
  onDelete,
  formatFileSize,
  formatDate,
}) {
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const isLinkDocument = doc.isLink === true || doc.linkType;

  const getLinkTypeLabel = () => {
    if (doc.linkType === 'google-drive') return 'Google Drive';
    if (doc.linkType === 'github') return 'GitHub';
    if (doc.linkType === 'external-link') return 'External Link';
    return 'Link';
  };

  const notes = doc.notes?.trim() || '';
  const notesTruncated = notes.length > MAX_NOTES_LENGTH;
  const notesDisplay =
    notesExpanded || !notesTruncated ? notes : `${notes.slice(0, MAX_NOTES_LENGTH)}...`;

  const visBadge = getDocumentVisibilityListBadge(doc);

  const primaryActions = (
    <>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => openDetails(doc)}
        className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-md shadow-blue-500/30 transition-all hover:bg-blue-700 hover:shadow-lg sm:px-4"
        title="View Full Details"
        aria-label="View details"
      >
        <HiInformationCircle className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Details</span>
      </motion.button>
      {!isLinkDocument && isPreviewable(doc.fileType) && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => openPreview(doc)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-xs font-semibold text-white shadow-md shadow-green-500/30 transition-all hover:bg-green-700 hover:shadow-lg sm:px-4"
          title="Preview Document"
          aria-label="Preview"
        >
          <HiEye className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Preview</span>
        </motion.button>
      )}
      <motion.a
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        href={doc.url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-md shadow-blue-500/30 transition-all hover:bg-blue-700 hover:shadow-lg sm:px-4"
        title={isLinkDocument ? 'Open Link' : 'Download'}
        aria-label={isLinkDocument ? 'Open link' : 'Download'}
      >
        {isLinkDocument ? (
          <>
            <HiGlobeAlt className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Open</span>
          </>
        ) : (
          <>
            <HiArrowDownTray className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Download</span>
          </>
        )}
      </motion.a>
    </>
  );

  const secondaryActions = (
    <>
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => openShare(doc)}
        className="rounded-lg border border-gray-300 bg-white p-2 text-gray-600 shadow-sm transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
        title="Share"
        aria-label="Share"
      >
        <HiLink className="h-4 w-4" />
      </motion.button>
      {canManageDoc(doc) && (
        <>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => openRename(doc)}
            className="rounded-lg border border-gray-300 bg-white p-2 text-gray-600 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50"
            title="Rename"
            aria-label="Rename"
          >
            <HiPencilSquare className="h-4 w-4" />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => openReupload(doc)}
            className="rounded-lg border border-gray-300 bg-white p-2 text-gray-600 shadow-sm transition-all hover:border-green-300 hover:bg-green-50 hover:text-green-600"
            title="Re-upload"
            aria-label="Re-upload"
          >
            <HiArrowPath className="h-4 w-4" />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onDelete(doc)}
            className="rounded-lg border border-red-300 bg-white p-2 text-red-600 shadow-sm transition-all hover:bg-red-50"
            title="Delete"
            aria-label="Delete"
          >
            <HiTrash className="h-4 w-4" />
          </motion.button>
        </>
      )}
    </>
  );

  return (
    <motion.div
      layout
      whileHover={{ x: 4 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      className="group flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-md shadow-gray-200/50 ring-1 ring-transparent transition-all hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/10 hover:ring-blue-100 sm:flex-row sm:items-center sm:gap-5 sm:p-5"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
        <motion.div
          whileHover={{ scale: 1.1, rotate: 5 }}
          transition={{ type: 'spring', stiffness: 400 }}
          className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl shadow-sm sm:h-14 sm:w-14 ${
            isLinkDocument
              ? 'bg-gradient-to-br from-blue-50 to-blue-100'
              : 'bg-gradient-to-br from-gray-50 to-gray-100'
          }`}
        >
          {isLinkDocument ? (
            <HiGlobeAlt className="h-6 w-6 text-blue-600 sm:h-7 sm:w-7" />
          ) : (
            <HiDocumentText className="h-6 w-6 text-gray-600 sm:h-7 sm:w-7" />
          )}
        </motion.div>
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-sm font-semibold text-gray-900" title={doc.fileName}>
            {doc.fileName}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 sm:gap-3">
            <span className="rounded-full bg-blue-50 px-2 py-0.5 font-medium text-blue-700">
              {doc.category}
            </span>
            {isLinkDocument && (
              <span className="font-medium text-indigo-600">{getLinkTypeLabel()}</span>
            )}
            {!isLinkDocument && <span>{formatFileSize(doc.fileSize)}</span>}
            <span>{formatDate(doc.uploadedAt)}</span>
            {visBadge && <span className={visBadge.className}>{visBadge.label}</span>}
          </div>
          {notes && (
            <div className="mt-2 text-xs text-gray-600">
              <p className={notesExpanded ? '' : 'line-clamp-2'}>{notesDisplay}</p>
              {notesTruncated && (
                <button
                  type="button"
                  onClick={() => setNotesExpanded(!notesExpanded)}
                  className="mt-1 inline-flex items-center gap-0.5 font-medium text-blue-600 hover:text-blue-700 hover:underline"
                >
                  {notesExpanded ? (
                    <>
                      Read less <HiChevronUp className="h-3.5 w-3.5" />
                    </>
                  ) : (
                    <>
                      Read more <HiChevronDown className="h-3.5 w-3.5" />
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 sm:border-t-0 sm:pt-0">
        {primaryActions}
        <div className="hidden items-center gap-2 lg:flex">{secondaryActions}</div>
        <div className="relative lg:hidden">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActionsOpen(!actionsOpen)}
            className="rounded-lg border border-gray-300 bg-white p-2 text-gray-600 shadow-sm hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
            title="More actions"
            aria-label="More actions"
            aria-expanded={actionsOpen}
          >
            <HiEllipsisVertical className="h-4 w-4" />
          </motion.button>
          <AnimatePresence>
            {actionsOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setActionsOpen(false)}
                  aria-hidden="true"
                />
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.95 }}
                  className="absolute right-0 top-full z-20 mt-2 flex gap-2 rounded-xl border border-gray-200 bg-white p-2 shadow-xl"
                >
                  {secondaryActions}
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
